import { Field, Formik, FormikErrors } from 'formik';
import {
	CerebroCheckbox,
	CerebroDatePicker,
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { DealStatusView } from '../../shared/models';
import { FormikUtils, NumberUtils } from '../../shared/utils';
import { Grid } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { getApiResponseData } from '../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

interface EditClosedFundedDealDrawerProps {
	show: boolean;
	data: DealStatusView;
	onClose: () => void;
}

export const EditClosedFundedDealDrawer: React.FC<EditClosedFundedDealDrawerProps> = ({ show, data, onClose }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const deal = data.deal;

	const editFundedDetailsMutation = useMutation(
		async ({ values }: EditFundedDealsFormMutationVariables) => {
			const patchValues = {
				amountFunded: NumberUtils.ParseDecimal(values.amountFunded, undefined),
				dateFunded: values.dateFunded,
				createSuccessFeeInvoice: values.createSuccessFeeInvoice,
				reasonForUsingCerebro: values.reasonForUsingCerebro,
				topDealChallenge1: values.topDealChallenge1,
				topDealChallenge2: values.topDealChallenge2,
				topDealChallenge3: values.topDealChallenge3,
				whySelectedTermSheet: values.whySelectedTermSheet,
				marketingOutreachOptOut: values.marketingOutreachOptOut,
				willingReferral: values.willingReferral
			};

			const { data, error } = await getApiResponseData<DealStatusView>(
				api.patch(CerebroApiEndpoints.Deals.UpdateClosedFunded(deal.guid), patchValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (_data, { helpers, values }) => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.Root(deal.guid));
				snackbar.enqueueSnackbar('Deal funded details successfully updated.', { variant: 'success' });
				helpers.resetForm({
					values: {
						amountFunded: values.amountFunded,
						dateFunded: values.dateFunded,
						createSuccessFeeInvoice: false,
						reasonForUsingCerebro: values.reasonForUsingCerebro,
						topDealChallenge1: values.topDealChallenge1,
						topDealChallenge2: values.topDealChallenge2,
						topDealChallenge3: values.topDealChallenge3,
						whySelectedTermSheet: values.whySelectedTermSheet,
						marketingOutreachOptOut: values.marketingOutreachOptOut,
						willingReferral: values.willingReferral
					},
				});
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer open={show} onClose={onClose}>
			<CerebroHeading>Edit Closed Funded Details</CerebroHeading>

			<Formik
				initialValues={
					{
						amountFunded: NumberUtils.FormatDecimal(deal.amountFunded),
						dateFunded: deal.dateFunded,
						createSuccessFeeInvoice: false,
						reasonForUsingCerebro: deal.reasonForUsingCerebro ?? '',
						topDealChallenge1: deal.topDealChallenge1 ?? '',
						topDealChallenge2: deal.topDealChallenge2 ?? '',
						topDealChallenge3: deal.topDealChallenge3 ?? '',
						whySelectedTermSheet: deal.whySelectedTermSheet ?? '',
						marketingOutreachOptOut: deal.marketingOutreachOptOut ?? false,
						willingReferral: deal.willingReferral ?? false,
					} as EditFundedDealsFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(editFundedDetailsMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel htmlFor="amountFunded">Amount Funded</CerebroLabel>
								<Field
									id="amountFunded"
									as={CerebroMaskedInput}
									name="amountFunded"
									disabled={isSubmitting}
									error={touched.amountFunded && errors.amountFunded}
								/>
								{touched.amountFunded && errors.amountFunded != null && (
									<CerebroText variant="error">{errors.amountFunded}</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel htmlFor="dateFunded">Date Funded</CerebroLabel>
								<Field
									id="dateFunded"
									component={CerebroDatePicker}
									name="dateFunded"
									disabled={isSubmitting}
									error={touched.dateFunded && errors.dateFunded}
								/>
								{touched.dateFunded && errors.dateFunded != null && (
									<CerebroText variant="error">{errors.dateFunded}</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel htmlFor="createSuccessFeeInvoice">
									Generate new success fee invoice based on the incremental amount?
								</CerebroLabel>
								<Field
									id="createSuccessFeeInvoice"
									as={CerebroYesNo}
									name="createSuccessFeeInvoice"
									disabled={isSubmitting}
								/>
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="reasonForUsingCerebro" sx={{ marginTop: 3 }}>
							Reason for using Cerebro
						</CerebroLabel>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Field
									id="reasonForUsingCerebro"
									as={CerebroTextarea}
									rows={3}
									name="reasonForUsingCerebro"
									disabled={isSubmitting}
								/>
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="topDealChallenge1" sx={{ marginTop: 3 }}>
							Top 3 Deal Challenges
						</CerebroLabel>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Field
									id="topDealChallenge1"
									as={CerebroTextarea}
									rows={2}
									name="topDealChallenge1"
									placeholder="Challenge 1"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={12}>
								<Field
									id="topDealChallenge2"
									as={CerebroTextarea}
									rows={2}
									name="topDealChallenge2"
									placeholder="Challenge 2"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={12}>
								<Field
									id="topDealChallenge3"
									as={CerebroTextarea}
									rows={2}
									name="topDealChallenge3"
									placeholder="Challenge 3"
									disabled={isSubmitting}
								/>
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="whySelectedTermSheet" sx={{ marginTop: 3 }}>
							Why did they select the chosen term sheet
						</CerebroLabel>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Field
									id="whySelectedTermSheet"
									as={CerebroTextarea}
									rows={3}
									name="whySelectedTermSheet"
									disabled={isSubmitting}
								/>
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={6}>
								<CerebroLabel htmlFor="marketingOutreachOptOut">
									<Field
										id="marketingOutreachOptOut"
										name="marketingOutreachOptOut"
										as={CerebroCheckbox}
										disabled={isSubmitting}
										sx={{ padding: 0, paddingRight: 1 }}
									/>
									<CerebroText variant="hint" component="span">
										Marketing Outreach Opt-out
									</CerebroText>
								</CerebroLabel>
							</Grid>
							<Grid item xs={6}>
								<CerebroLabel htmlFor="willingReferral">
									<Field
										id="willingReferral"
										name="willingReferral"
										as={CerebroCheckbox}
										disabled={isSubmitting}
										sx={{ padding: 0, paddingRight: 1 }}
									/>
									<CerebroText variant="hint" component="span">
										Willing to be a reference?
									</CerebroText>
								</CerebroLabel>
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

var validateForm = (values: EditFundedDealsFormValues): EditFundedDealsFormErrors => {
	const errors: EditFundedDealsFormErrors = {};

	const amountFunded = NumberUtils.ParseDecimal(values.amountFunded, undefined);
	if (!amountFunded || amountFunded <= 0) {
		errors.amountFunded = 'Valid funded amount is required.';
	}

	if (!values.dateFunded) {
		errors.dateFunded = 'Must select a valid date funded.';
	} else {
		const dateError = validateDateWithinPlusMinus100Years(values.dateFunded, 'Date funded');
		if (dateError) {
			errors.dateFunded = dateError;
		}
	}

	return errors;
};

type EditFundedDealsFormErrors = FormikErrors<EditFundedDealsFormValues>;
type EditFundedDealsFormMutationVariables = FormikUtils.MutateOnSubmitVariables<EditFundedDealsFormValues>;
interface EditFundedDealsFormValues {
	amountFunded?: string;
	dateFunded: Date | null;
	createSuccessFeeInvoice: boolean;
	reasonForUsingCerebro?: string;
	topDealChallenge1?: string;
	topDealChallenge2?: string;
	topDealChallenge3?: string;
	whySelectedTermSheet?: string;
	marketingOutreachOptOut: boolean;
	willingReferral: boolean;
}
